/**
 * MCP Service API Client
 * Handles all MCP (AI Chat) related API requests
 */

import apiClient from '../client';
import type {
  MCPChatResponse,
  MCPConfirmActionResponse,
  ToolsListResponse,
  HealthCheckResponse,
} from '../types/mcp.types';

const MCP_BASE_URL = '/mcp';

// ============================================================================
// Chat Operations
// ============================================================================

/**
 * Send a message to the MCP AI assistant
 * @param message - User's message
 * @param confirmed - Whether action is pre-confirmed (default: false)
 */
export const chat = async (
  message: string,
  confirmed: boolean = false
): Promise<MCPChatResponse> => {
  const response = await apiClient.post<MCPChatResponse>(
    `${MCP_BASE_URL}/chat`,
    {
      message,
      confirmed,
    }
  );
  return response.data;
};

/**
 * Confirm or cancel a pending action
 * @param toolName - Name of the tool to execute
 * @param toolArgs - Arguments for the tool
 * @param confirmed - Whether user confirmed (true) or cancelled (false)
 */
export const confirmAction = async (
  toolName: string,
  toolArgs: Record<string, any>,
  confirmed: boolean
): Promise<MCPConfirmActionResponse> => {
  const response = await apiClient.post<MCPConfirmActionResponse>(
    `${MCP_BASE_URL}/confirm-action`,
    {
      tool_name: toolName,
      tool_args: toolArgs,
      confirmed,
    }
  );
  return response.data;
};

// ============================================================================
// Tools Operations
// ============================================================================

/**
 * Get list of available MCP tools
 */
export const getTools = async (): Promise<ToolsListResponse> => {
  const response = await apiClient.get<ToolsListResponse>(
    `${MCP_BASE_URL}/tools`
  );
  return response.data;
};

// ============================================================================
// Health Check
// ============================================================================

/**
 * Basic health check
 */
export const healthCheck = async (): Promise<HealthCheckResponse> => {
  const response = await apiClient.get<HealthCheckResponse>(
    `${MCP_BASE_URL}/health`
  );
  return response.data;
};

/**
 * Liveness probe (for Kubernetes)
 */
export const healthLive = async (): Promise<HealthCheckResponse> => {
  const response = await apiClient.get<HealthCheckResponse>(
    `${MCP_BASE_URL}/health/live`
  );
  return response.data;
};

/**
 * Readiness probe (checks DB connection)
 */
export const healthReady = async (): Promise<HealthCheckResponse> => {
  const response = await apiClient.get<HealthCheckResponse>(
    `${MCP_BASE_URL}/health/ready`
  );
  return response.data;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if MCP service is available
 */
export const isMCPAvailable = async (): Promise<boolean> => {
  try {
    const health = await healthCheck();
    return health.status === 'healthy';
  } catch (error) {
    return false;
  }
};
